import { useEffect, useCallback, useRef, useState } from 'react';

interface FocusManagerOptions {
  onFocusLost?: () => void;
  onFocusGained?: () => void;
  pauseOnFocusLoss?: boolean;
  debounceMs?: number;
  onPause?: () => void;
  onResume?: () => void;
  gameMode?: boolean; // Special handling for game screens
}

export const useFocusManager = ({
  onFocusLost,
  onFocusGained,
  pauseOnFocusLoss = true,
  debounceMs = 100,
  onPause,
  onResume,
  gameMode = false,
}: FocusManagerOptions = {}) => {
  const isDocumentVisible = useRef<boolean>(true);
  const debounceTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [pauseReason, setPauseReason] = useState<'focus' | 'manual' | 'none'>('none');
  const gameTimersRef = useRef<Set<NodeJS.Timeout>>(new Set());
  const gameIntervalsRef = useRef<Set<NodeJS.Timeout>>(new Set());

  // Pause game function with timer management
  const pauseGame = useCallback((reason: 'focus' | 'manual') => {
    if (isPaused) return;
    
    setIsPaused(true);
    setPauseReason(reason);
    
    // In game mode, pause all timers and intervals
    if (gameMode) {
      // Dispatch pause event for game engine and other components
      window.dispatchEvent(new CustomEvent('game-pause', {
        detail: { reason, timestamp: Date.now() }
      }));
    }
    
    onPause?.();
    console.log(`[FOCUS] Game paused - reason: ${reason}`);
  }, [isPaused, gameMode, onPause]);

  // Resume game function
  const resumeGame = useCallback(() => {
    if (!isPaused) return;
    
    setIsPaused(false);
    setPauseReason('none');
    
    // In game mode, resume all timers and intervals
    if (gameMode) {
      // Dispatch resume event
      window.dispatchEvent(new CustomEvent('game-resume', {
        detail: { timestamp: Date.now() }
      }));
    }
    
    onResume?.();
    console.log('[FOCUS] Game resumed');
  }, [isPaused, gameMode, onResume]);

  const handleVisibilityChange = useCallback(() => {
    // Clear any existing debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Debounce the visibility change to avoid rapid firing
    debounceTimer.current = setTimeout(() => {
      const isVisible = !document.hidden;
      const wasPreviouslyVisible = isDocumentVisible.current;

      // Only trigger if visibility actually changed
      if (isVisible !== wasPreviouslyVisible) {
        isDocumentVisible.current = isVisible;

        if (!isVisible && pauseOnFocusLoss) {
          console.log('[FOCUS] Document lost focus - triggering pause');
          pauseGame('focus');
          onFocusLost?.();
        } else if (isVisible && wasPreviouslyVisible === false) {
          console.log('[FOCUS] Document gained focus - resuming');
          resumeGame();
          onFocusGained?.();
        }
      }
    }, debounceMs);
  }, [onFocusLost, onFocusGained, pauseOnFocusLoss, debounceMs, pauseGame, resumeGame]);

  const handleWindowBlur = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (pauseOnFocusLoss && isDocumentVisible.current) {
        console.log('[FOCUS] Window lost focus - triggering pause');
        isDocumentVisible.current = false;
        pauseGame('focus');
        onFocusLost?.();
      }
    }, debounceMs);
  }, [onFocusLost, pauseOnFocusLoss, debounceMs, pauseGame]);

  const handleWindowFocus = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (!isDocumentVisible.current) {
        console.log('[FOCUS] Window gained focus - resuming');
        isDocumentVisible.current = true;
        resumeGame();
        onFocusGained?.();
      }
    }, debounceMs);
  }, [onFocusGained, debounceMs, resumeGame]);

  // Add keyboard shortcuts for pause/resume
  useEffect(() => {
    if (!gameMode) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // ESC to pause manually, Space to toggle pause
      if (event.code === 'Escape' && !isPaused) {
        event.preventDefault();
        pauseGame('manual');
      } else if (event.code === 'Space' && isPaused && pauseReason === 'manual') {
        event.preventDefault();
        resumeGame();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameMode, isPaused, pauseReason, pauseGame, resumeGame]);

  useEffect(() => {
    // Set initial visibility state
    isDocumentVisible.current = !document.hidden;

    // Listen for visibility changes (tab switching, minimize, etc.)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for window focus events (clicking away from browser)
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    // Mobile-specific events
    window.addEventListener('pagehide', handleWindowBlur);
    window.addEventListener('pageshow', handleWindowFocus);

    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('pagehide', handleWindowBlur);
      window.removeEventListener('pageshow', handleWindowFocus);
    };
  }, [handleVisibilityChange, handleWindowBlur, handleWindowFocus]);

  // Return enhanced state and control functions
  return {
    // State
    isDocumentVisible: isDocumentVisible.current,
    isPaused,
    pauseReason,
    
    // Manual control functions
    pause: useCallback(() => pauseGame('manual'), [pauseGame]),
    resume: resumeGame,
    
    // Legacy functions for backward compatibility
    forcePause: useCallback(() => {
      isDocumentVisible.current = false;
      pauseGame('focus');
      onFocusLost?.();
    }, [onFocusLost, pauseGame]),
    
    forceResume: useCallback(() => {
      isDocumentVisible.current = true;
      resumeGame();
      onFocusGained?.();
    }, [onFocusGained, resumeGame]),
  };
};